import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CheckCircle, XCircle, Clock, FileText, Download } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useAppStore } from '@/lib/data';
import { groupsApi } from '@/lib/kwacha-ai-client';
import { useAuth } from '@/hooks/use-auth';
import type { GroupContribution, SavingsGroup } from '@/lib/types';

interface PendingContributionsProps {
  group: SavingsGroup;
  trigger?: React.ReactNode;
}

export function PendingContributions({ group, trigger }: PendingContributionsProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { groupContributions, updateGroupContribution, updateSavingsGroup, addGroupActivity } = useAppStore();

  const [selectedContribution, setSelectedContribution] = useState<GroupContribution | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  const pendingContributions = groupContributions.filter(
    c => c.groupId === group.id && c.status === 'pending'
  );

  if (pendingContributions.length === 0) return null;

  const handleApprove = async (contribution: GroupContribution) => {
    setIsVerifying(true);
    try {
      await groupsApi.approveContribution({
        contribution_id: contribution.id,
        confirmed_by: user?.id ?? '',
      });
      // Optimistic local update
      updateGroupContribution({
        ...contribution,
        status: 'confirmed',
        confirmedBy: user?.id,
        confirmedAt: new Date().toISOString(),
      });
      updateSavingsGroup({
        ...group,
        currentAmount: group.currentAmount + contribution.amount,
        updatedAt: new Date().toISOString(),
      });
      toast({
        title: 'Contribution approved!',
        description: `${formatCurrency(contribution.amount)} added to group total.`,
      });
      setSelectedContribution(null);
    } catch (error: any) {
      toast({
        title: 'Approval failed',
        description: error?.message ?? 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleReject = async () => {
    if (!selectedContribution || !rejectionReason.trim()) {
      toast({ title: 'Rejection reason required', description: 'Please provide a reason.', variant: 'destructive' });
      return;
    }
    setIsVerifying(true);
    try {
      updateGroupContribution({
        ...selectedContribution,
        status: 'rejected',
        rejectionReason: rejectionReason.trim(),
      });
      addGroupActivity({
        groupId: group.id,
        type: 'contribution_made',
        userId: selectedContribution.memberId,
        description: `Contribution of ${formatCurrency(selectedContribution.amount)} was rejected`,
        metadata: {
          contributionId: selectedContribution.id,
          amount: selectedContribution.amount,
          rejectionReason: rejectionReason.trim(),
        },
        createdAt: new Date().toISOString(),
      });
      toast({ title: 'Contribution rejected', description: 'The member will be notified.' });
      setSelectedContribution(null);
      setShowRejectDialog(false);
      setRejectionReason('');
    } catch {
      toast({ title: 'Error rejecting contribution', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setIsVerifying(false);
    }
  };

  const defaultTrigger = (
    <Button variant="outline" size="sm" className="relative">
      <Clock className="h-3 w-3 mr-2" />
      Pending Review
      {pendingContributions.length > 0 && (
        <Badge variant="destructive" className="absolute -top-2 -right-2 h-5 w-5 p-0 text-xs">
          {pendingContributions.length}
        </Badge>
      )}
    </Button>
  );

  return (
    <>
      <Dialog open={!!selectedContribution} onOpenChange={(open) => !open && setSelectedContribution(null)}>
        <DialogTrigger asChild>
          {trigger || defaultTrigger}
        </DialogTrigger>

        {selectedContribution && (
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Review Contribution
              </DialogTitle>
              <DialogDescription>Review the contribution details and proof of payment</DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              <Card>
                <CardHeader><CardTitle className="text-lg">Contribution Details</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Amount</Label>
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedContribution.amount)}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Payment Method</Label>
                      <p className="capitalize">{selectedContribution.method.replace('_', ' ')}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Submitted</Label>
                    <p>{formatDate(selectedContribution.contributedAt)}</p>
                  </div>
                  {selectedContribution.description && (
                    <div>
                      <Label className="text-sm font-medium">Description</Label>
                      <p className="text-muted-foreground">{selectedContribution.description}</p>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="capitalize">{selectedContribution.status}</Badge>
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Awaiting verification</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-lg">Proof of Payment</CardTitle></CardHeader>
                <CardContent>
                  {selectedContribution.proofFile ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                        <div className="flex items-center gap-3">
                          <FileText className="h-8 w-8 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{selectedContribution.proofFile}</p>
                            <p className="text-sm text-muted-foreground">Proof document</p>
                          </div>
                        </div>
                        <Button variant="outline" size="sm"><Download className="h-3 w-3 mr-2" />View</Button>
                      </div>
                      {selectedContribution.proofUrl && (
                        <img src={selectedContribution.proofUrl} alt="Proof of payment" className="max-w-full h-auto rounded-lg border" />
                      )}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">No proof of payment uploaded</p>
                  )}
                </CardContent>
              </Card>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowRejectDialog(true)} className="flex-1" disabled={isVerifying}>
                  <XCircle className="h-3 w-3 mr-2" />Reject
                </Button>
                <Button onClick={() => handleApprove(selectedContribution)} className="flex-1" disabled={isVerifying}>
                  <CheckCircle className="h-3 w-3 mr-2" />{isVerifying ? 'Approving...' : 'Approve'}
                </Button>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>

      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Reject Contribution</DialogTitle>
            <DialogDescription>Provide a reason — this will be shared with the member.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">Rejection Reason *</Label>
              <Textarea
                id="rejection-reason"
                placeholder="Explain why this contribution is being rejected..."
                rows={4}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => { setShowRejectDialog(false); setRejectionReason(''); }} className="flex-1" disabled={isVerifying}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleReject} className="flex-1" disabled={isVerifying || !rejectionReason.trim()}>
                {isVerifying ? 'Rejecting...' : 'Reject Contribution'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
